import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import { Box, IconButton, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { CreateVaccine } from 'features/vaccine';
import { EditVaccine } from 'features/vaccine/ui/edit-vaccine';

import { EditDiseaseDialog, diseaseModel } from 'entities/disease';
import { userModel } from 'entities/user';
import { VaccineTablePaginated, vaccineModel } from 'entities/vaccine';

import { useDebouncedSearch } from 'shared/lib';
import { LoadingProgress } from 'shared/ui';

export const VaccineTab = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Получаем параметры из URL или используем значения по умолчанию
  const [filter, setFilter] = useState(searchParams.get('filter') ?? '');
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') ?? 'name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(
    (searchParams.get('sortOrder') as 'asc' | 'desc') ?? 'asc',
  );
  const [page, setPage] = useState(parseInt(searchParams.get('page') ?? '1', 10));
  const [rowsPerPage, setRowsPerPage] = useState(parseInt(searchParams.get('limit') ?? '20', 10));

  // Состояние для редактирования
  const [editVaccineId, setEditVaccineId] = useState<string | null>(null);
  const [selectedDiseaseId, setSelectedDiseaseId] = useState<string | null>(null);
  const [loadingEditId, setLoadingEditId] = useState<string | null>(null);
  const [loadingDiseaseId, setLoadingDiseaseId] = useState<string | null>(null);

  const { data: userData } = userModel.useUser();

  // Мутации
  const {
    mutate: createVaccine,
    isSuccess: isCreateSuccess,
    isLoading: isCreating,
  } = vaccineModel.postNewVaccineAsync();
  const {
    mutate: updateVaccine,
    isSuccess: isUpdateSuccess,
    isLoading: isUpdating,
  } = vaccineModel.updateVaccineAsync();
  const {
    mutate: updateDisease,
    isSuccess: isDiseaseUpdateSuccess,
    isLoading: isDiseaseUpdating,
  } = diseaseModel.updateDiseaseAsync();

  // Используем debounce для фильтра чтобы не запускать запрос при каждом символе
  const debouncedFilter = useDebouncedSearch(filter, 400);

  // Создаем объект параметров для API с использованием useMemo
  const params = useMemo(
    () => ({
      filter: debouncedFilter || undefined,
      sortBy,
      sortOrder,
      page,
      limit: rowsPerPage,
    }),
    [debouncedFilter, sortBy, sortOrder, page, rowsPerPage],
  );

  const { isLoading, isFetching } = vaccineModel.getVaccinesAllAsync(params)(dispatch);
  const { vaccineList, pagination } = vaccineModel.useVaccineList();

  // Обновляем URL при изменении параметров (используем debouncedFilter для URL)
  useEffect(() => {
    const newParams: Record<string, string> = {
      page: page.toString(),
      limit: rowsPerPage.toString(),
      sortBy,
      sortOrder,
    };

    if (debouncedFilter) newParams.filter = debouncedFilter;

    setSearchParams(newParams, { replace: true });
  }, [debouncedFilter, sortBy, sortOrder, page, rowsPerPage, setSearchParams]);

  // Закрываем диалоги при успешном обновлении
  useEffect(() => {
    if (isUpdateSuccess) {
      setEditVaccineId(null);
    }
  }, [isUpdateSuccess]);

  useEffect(() => {
    if (isDiseaseUpdateSuccess) {
      setSelectedDiseaseId(null);
      // Обновляем таблицу вакцин, так как болезнь могла измениться
      void queryClient.invalidateQueries([vaccineModel.VACCINES_ALL_QUERY_KEY]);
    }
  }, [isDiseaseUpdateSuccess, queryClient]);

  // Мемоизируем обработчики с помощью useCallback
  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  }, []);

  const handleFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(e.target.value);
    setPage(1);
  }, []);

  const handleClearFilter = useCallback(() => {
    setFilter('');
    setPage(1);
  }, []);

  const handleSortChange = useCallback(
    (columnId: string) => {
      if (sortBy === columnId) {
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        setSortBy(columnId);
        setSortOrder('asc');
      }
      setPage(1);
    },
    [sortBy, sortOrder],
  );

  const handleEditVaccine = useCallback((vaccineId: string) => {
    setLoadingEditId(vaccineId);
    setEditVaccineId(vaccineId);
  }, []);

  const handleDataLoaded = useCallback(() => {
    setLoadingEditId(null);
  }, []);

  const handleCloseEdit = useCallback(() => {
    setEditVaccineId(null);
    setLoadingEditId(null);
  }, []);

  const handleDiseaseClick = useCallback((diseaseId: string) => {
    setLoadingDiseaseId(diseaseId);
    setSelectedDiseaseId(diseaseId);
  }, []);

  const handleDiseaseDataLoaded = useCallback(() => {
    setLoadingDiseaseId(null);
  }, []);

  const handleCloseDiseaseDialog = useCallback(() => {
    setSelectedDiseaseId(null);
    setLoadingDiseaseId(null);
  }, []);

  // Показываем LoadingProgress только при первой загрузке (когда еще нет данных)
  if (isLoading && !vaccineList.length) return <LoadingProgress />;

  return (
    <Box display="flex" flexDirection="column">
      <Paper>
        <Box display="flex" alignItems="center" p="8px 16px">
          <Typography variant="h6">Human Vaccine control panel</Typography>
          <Box display="flex" alignItems="center" ml={3} mr="auto">
            <TextField
              sx={{ minWidth: '350px', width: '100%' }}
              placeholder="Enter the name of the vaccine"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: filter && (
                  <IconButton onClick={handleClearFilter}>
                    <CloseIcon />
                  </IconButton>
                ),
              }}
              size="small"
              value={filter}
              onChange={handleFilterChange}
            />
            <Typography px={2} width="100%">
              {pagination?.total ?? 0} vaccines
            </Typography>
          </Box>
          <CreateVaccine onSubmit={createVaccine} isSuccess={isCreateSuccess} isLoading={isCreating} />
        </Box>
      </Paper>
      <Box mt={1}>
        <VaccineTablePaginated
          list={vaccineList}
          pagination={pagination}
          page={page}
          rowsPerPage={rowsPerPage}
          sortBy={sortBy}
          sortOrder={sortOrder}
          isLoading={isFetching}
          loadingEditId={loadingEditId}
          loadingDiseaseId={loadingDiseaseId}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSortChange={handleSortChange}
          onEdit={handleEditVaccine}
          onDiseaseClick={handleDiseaseClick}
        />
      </Box>

      {/* Edit Vaccine Dialog */}
      {editVaccineId && (
        <EditVaccine
          vaccineId={editVaccineId}
          isOpen={true}
          onClose={handleCloseEdit}
          updateFn={updateVaccine}
          isLoadingUpdate={isUpdating}
          isSuccessUpdate={isUpdateSuccess}
          onDataLoaded={handleDataLoaded}
        />
      )}

      {/* Edit Disease Dialog */}
      {selectedDiseaseId && (
        <EditDiseaseDialog
          diseaseId={selectedDiseaseId}
          isPets={false}
          userRole={userData?.role}
          isLoading={isDiseaseUpdating}
          onClose={handleCloseDiseaseDialog}
          onVerified={updateDisease}
          onSubmit={updateDisease}
          onDataLoaded={handleDiseaseDataLoaded}
        />
      )}
    </Box>
  );
};
